import { useSetAtom } from "jotai";
import { type FocusEvent, type KeyboardEvent, useEffect, useRef } from "react";
import { canv } from "@/canv/global";
import { widgetMenuOptionsAtom } from "@/interface/AppStore";
import type { DocumentTurnPageOption, widgetOption } from "@/widgetMenu/widgetMenu";
import { turnPage } from "../../lib/turnPage";
import styles from "./DocumentTurnPage.module.css";

const INPUT_PREVENT_DEFAULT_KEYS = ["e", "E", ".", ",", "_"];

interface PageInputProps {
    options: widgetOption<DocumentTurnPageOption>[];
}

export function PageInput({ options }: PageInputProps) {
    const inputRef = useRef<HTMLInputElement>(null);

    const setWidgetMenuOptions = useSetAtom(widgetMenuOptionsAtom);

    const handleInputFocus = () => {
        canv.appInterface.inputFocused = 1;
    };

    const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
        canv.appInterface.inputFocused = 0;

        const value = Number(event.currentTarget.value);

        turnPage(value, canv);

        setWidgetMenuOptions((prevMap) => {
            const newMap = new Map(prevMap);
            const options = prevMap.get(
                "documentTurnPage",
            ) as widgetOption<DocumentTurnPageOption>[];
            options[0].value.currentPageNumber = value;
            newMap.set("documentTurnPage", options);

            return newMap;
        });
    };

    const handleInputKeydown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" || event.key === "Tab") {
            event.preventDefault();
            const value = Number(event.currentTarget.value);

            turnPage(value, canv);

            setWidgetMenuOptions((prevMap) => {
                const newMap = new Map(prevMap);
                const options = prevMap.get(
                    "documentTurnPage",
                ) as widgetOption<DocumentTurnPageOption>[];
                options[0].value.currentPageNumber = value;
                newMap.set("documentTurnPage", options);

                return newMap;
            });
        }
        if (INPUT_PREVENT_DEFAULT_KEYS.includes(event.key)) {
            event.preventDefault();
        }
    };

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.value = String(options[0].value.currentPageNumber);
        }
    }, [options[0].value.currentPageNumber]);

    return (
        <input
            className={styles.input}
            type="number"
            inputMode="numeric"
            pattern="\\d*"
            step="1"
            min="1"
            max={options[0].value.totalPages}
            defaultValue={options[0].value.currentPageNumber}
            ref={inputRef}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeydown}
        />
    );
}
